import { useEffect, useRef, useState } from "react";
import { debounce } from "lodash";
import useApiClient from "./useApiClient";
import { useTranslation } from "react-i18next";
import { useHotelContext } from "./hotel/useHotelContext";
import { toast } from "react-toastify";

const airports = [];

const useAutoComplete = ({ url, mapResponseToOptions }) => {
    const { i18n } = useTranslation();
    const [term, setTerm] = useState("");
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const { api } = useApiClient();
    const { setProviders } = useHotelContext();
    const handleSearchDebounced = useRef(
        debounce((searchTerm) => getResults(searchTerm), 300)
    ).current;

    useEffect(() => {
        if (!url.includes("cityAutocomplete")) {
            setResults(airports);
        }
    }, [url]);

    const getResults = async (searchTerm) => {
        if (searchTerm.trim().length < 2) {
            return;
        }
        setLoading(true);
        try {
            const res = await api.get(url, {
                params: {
                    term: searchTerm,
                    language: i18n.language,
                },
            });

            const options = mapResponseToOptions(res?.data?.predictions || []);
            setResults(options);

            if (url.includes("cityAutocomplete")) {
                const providers = res.data?.providers.map((p) => {
                    return {
                        id: p.id,
                        code: p.code,
                    };
                });
                setProviders(providers);
            }
        } catch (error) {
            console.error("Error fetching autocomplete data:", error);
            if (error?.code === "ERR_NETWORK") {
                toast.error("Network error, please try again later.");
            }
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (inputText, meta) => {
        if (meta?.action === "input-blur" || meta?.action === "menu-close") {
            return;
        }
        setTerm(inputText);
        if (inputText.trim().length === 0) {
            setResults([]);
            return;
        }
        handleSearchDebounced(inputText);
    };

    return {
        term,
        results,
        loading,
        handleInputChange,
        fetchResults: getResults,
        setTerm,
        setResults,
    };
};

export default useAutoComplete;
